import { createFileRoute, Outlet, redirect, useBlocker, useNavigate, useRouterState } from "@tanstack/react-router";
import * as React from "react";
import { supabase } from "@/integrations/supabase/client";
import { AppShell } from "@/components/app-shell";
import { GlobalCallCenter } from "@/components/chat/GlobalCallCenter";
import { hasOpenOverlay } from "@/hooks/use-history-back-close";
import { useFcmToken } from "@/hooks/use-fcm-token";
import { setCacheUser } from "@/hooks/use-cached-state";
import { primeUserRoleCache } from "@/hooks/use-user-role";
import { primeUnreadChatsCache } from "@/hooks/use-unread-chats";

const HOME_PATH = "/dashboard";

function BackToHomeGuard() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const { status, reset } = useBlocker({
    shouldBlockFn: ({ next, action }) => {
      // Only intercept browser back / forward (BACK/FORWARD). Regular Link
      // taps (PUSH / REPLACE) navigate normally.
      if (action !== "BACK" && action !== "FORWARD") return false;
      // If a dialog/sheet/chat room is open, let its own blocker close it.
      if (hasOpenOverlay()) return false;
      // Already on home — nothing to intercept.
      if (pathname === HOME_PATH) return false;
      // Already going to home — allow.
      if (next.pathname === HOME_PATH) return false;
      return true;
    },
    withResolver: true,
    enableBeforeUnload: false,
  });

  React.useEffect(() => {
    if (status === "blocked") {
      reset();
      navigate({ to: HOME_PATH });
    }
  }, [status, reset, navigate]);

  return null;
}

export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  beforeLoad: async () => {
    // Use getSession() — reads from localStorage, no network round-trip.
    // getUser() hits Supabase auth on every navigation and makes routing slow.
    const { data } = await supabase.auth.getSession();
    const user = data.session?.user;
    if (user) {
      setCacheUser(user.id);
      primeUserRoleCache(user.id);
      primeUnreadChatsCache(user.id);
      return { user };
    }
    throw redirect({ to: "/auth" });
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  useFcmToken();
  return (
    <AppShell>
      <BackToHomeGuard />
      <Outlet />
      <GlobalCallCenter />
    </AppShell>
  );
}
